/**
 * Version History Modal - Display declaration version history with changes
 */
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../../store/authStore';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  History as HistoryIcon,
  Person as PersonIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';

interface VersionHistory {
  id: string;
  declaration_id: string;
  version_number: number;
  changed_fields: Record<string, { old: any; new: any }>;
  changed_by: string;
  changed_at: string;
  change_reason?: string;
}

interface VersionHistoryModalProps {
  open: boolean;
  declarationId: string;
  onClose: () => void;
}

const VersionHistoryModal: React.FC<VersionHistoryModalProps> = ({
  open,
  declarationId,
  onClose,
}) => {
  const token = useAuthStore((state) => state.token);
  const [versions, setVersions] = useState<VersionHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && declarationId) {
      fetchVersionHistory();
    }
  }, [open, declarationId]);

  const fetchVersionHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/v1/supplier-declarations/declarations/${declarationId}/history`,
        {
          headers: {
            Authorization: `Bearer ${token?.access_token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch version history');
      }

      const data = await response.json();
      setVersions(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') {
      return '(empty)';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderChangedFields = (changedFields: Record<string, { old: any; new: any }>) => {
    const fieldEntries = Object.entries(changedFields);

    if (fieldEntries.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No field changes recorded
        </Typography>
      );
    }

    return (
      <Box sx={{ mt: 1 }}>
        {fieldEntries.map(([fieldName, change]) => (
          <Box
            key={fieldName}
            sx={{
              mb: 1.5,
              p: 1.5,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="subtitle2" color="primary" gutterBottom>
              {formatFieldName(fieldName)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={formatValue(change.old)}
                size="small"
                sx={{
                  bgcolor: 'error.50',
                  color: 'error.dark',
                  textDecoration: 'line-through',
                }}
              />
              <Typography variant="body2" color="text.secondary">
                →
              </Typography>
              <Chip
                label={formatValue(change.new)}
                size="small"
                sx={{
                  bgcolor: 'success.50',
                  color: 'success.dark',
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon />
          <Typography variant="h6">Version History</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && versions.length === 0 && (
          <Alert severity="info">
            No version history available for this declaration.
          </Alert>
        )}

        {!loading && !error && versions.length > 0 && (
          <Timeline position="right">
            {versions.map((version, index) => (
              <TimelineItem key={version.id}>
                <TimelineOppositeContent
                  sx={{ flex: 0.3, py: 2 }}
                  color="text.secondary"
                >
                  <Typography variant="body2" fontWeight="bold">
                    Version {version.version_number}
                  </Typography>
                  <Typography variant="caption">
                    {formatDate(version.changed_at)}
                  </Typography>
                </TimelineOppositeContent>

                <TimelineSeparator>
                  <TimelineDot color={index === 0 ? 'primary' : 'grey'}>
                    <PersonIcon fontSize="small" />
                  </TimelineDot>
                  {index < versions.length - 1 && <TimelineConnector />}
                </TimelineSeparator>

                <TimelineContent sx={{ py: 2 }}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Changed by: {version.changed_by}
                      </Typography>
                      {index === 0 && (
                        <Chip label="Latest" color="primary" size="small" />
                      )}
                    </Box>

                    {version.change_reason && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1, fontStyle: 'italic' }}
                      >
                        Reason: {version.change_reason}
                      </Typography>
                    )}

                    <Divider sx={{ my: 1 }} />

                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      Changes:
                    </Typography>
                    {renderChangedFields(version.changed_fields)}

                    {/* Optional: Add View Version button for future enhancement */}
                    {/* <Button
                      size="small"
                      startIcon={<ViewIcon />}
                      sx={{ mt: 1 }}
                      onClick={() => {
                        // Future: Implement view full declaration at this version
                        console.log('View version:', version.version_number);
                      }}
                    >
                      View Full Declaration
                    </Button> */}
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VersionHistoryModal;
